import { useCallback, useEffect, useRef, useState } from "react";

/** How close to the bottom still counts as following the stream. */
const NEAR_BOTTOM_PX = 120;

/**
 * The message the "new messages" hint should jump to: the FIRST one that arrived while the reader was away,
 * i.e. the message right after the one that was newest when they scrolled up (`previousId`). Jumping to the
 * literal newest would skip everything between, which is the whole point of the hint. Falls back to the
 * newest when `previousId` can't be located (it paged out of the loaded window), since there's nothing
 * better to anchor on. Null when there are no messages.
 */
export function firstNewMessageId(messages: { id: string }[], previousId: string | undefined): string | null {
  const newest = messages[messages.length - 1];
  if (!newest) return null;
  const prevIndex = messages.findIndex((m) => m.id === previousId);
  return prevIndex >= 0 && prevIndex + 1 < messages.length ? messages[prevIndex + 1].id : newest.id;
}

/**
 * Keeps a message list pinned to the newest only while the reader is actually at the end. Reading further
 * up is deliberate, so an arriving message announces itself instead of dragging the view off what's being
 * read — the same rule the phone applies, kept in one place here so both the main chat and idea channels
 * behave identically.
 *
 * `firstNewId` is the first message that arrived while away, which is what the hint jumps to; the newest
 * would skip whatever came before it. Null once the end has been reached again, since arriving there is
 * the same thing as having seen them.
 */
export function useFollowStream(
  containerRef: { current: HTMLElement | null },
  messages: { id: string; role?: string }[],
  /** Changing this (e.g. the open session/channel) RESETS the follow state so the next load lands at the
   *  bottom. Without it, `nearBottom` carries over from the previous chat — if you'd scrolled up there, the
   *  newly-opened chat wrongly opens not-at-bottom. */
  resetKey?: string,
  /**
   * True while the CALLER is still placing the view for a freshly opened list (e.g. on its first unread).
   * Following runs in a passive effect, which fires after the caller's layout effect — so without this it
   * scrolls to the end immediately after the caller anchored, losing the anchor and showing both positions
   * one after the other. Arrivals are still recorded meanwhile, so nothing is later mistaken for new.
   */
  initialPending = false,
) {
  const nearBottom = useRef(true);
  const lastNewestId = useRef<string | undefined>(messages[messages.length - 1]?.id);
  const [firstNewId, setFirstNewId] = useState<string | null>(null);

  useEffect(() => {
    nearBottom.current = true;
    lastNewestId.current = undefined; // the next arrival is treated as fresh → the messages effect scrolls to end
    setFirstNewId(null);
  }, [resetKey]);

  /** Call from the container's onScroll. */
  const onScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    if (nearBottom.current) setFirstNewId(null);
  }, [containerRef]);

  useEffect(() => {
    const el = containerRef.current;
    const newest = messages[messages.length - 1];
    if (!el || !newest) return;
    const previousId = lastNewestId.current;
    if (newest.id === previousId) return;
    lastNewestId.current = newest.id;
    if (initialPending) return; // the caller is placing this view; taking it now would undo that
    // Your own message always returns you to the end — announcing something you just sent is nonsense.
    if (newest.role === "user" || nearBottom.current) {
      setFirstNewId(null);
      const node = el.lastElementChild as HTMLElement | null;
      // A message taller than the viewport is aligned to its top, so it's read from the beginning rather
      // than arriving already scrolled past its own opening.
      if (node && node.offsetHeight > el.clientHeight) el.scrollTop = node.offsetTop;
      else el.scrollTop = el.scrollHeight;
    } else if (firstNewId === null) {
      setFirstNewId(firstNewMessageId(messages, previousId));
    }
    // `firstNewId` is deliberately not a dependency: it's set here and reading it would re-run this on the
    // hint appearing, which would then treat the same arrival as a second one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, containerRef, initialPending]);

  // An image or code block that lays out after its message does grows the list UNDER the reader, which leaves
  // a view that was at the end sitting short of it — the message that just arrived is then partly below the
  // fold. Re-pin while they're still at the end; scrolled-up readers are left alone, as everywhere else here.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const obs = new ResizeObserver(() => {
      if (nearBottom.current) el.scrollTop = el.scrollHeight;
    });
    for (const child of Array.from(el.children)) obs.observe(child);
    return () => obs.disconnect();
  }, [containerRef, messages]);

  /** Scroll the first message that arrived while away to the top of the viewport, and clear the hint. */
  const jumpToFirstNew = useCallback(() => {
    const el = containerRef.current;
    if (!el || !firstNewId) return;
    const node = el.querySelector<HTMLElement>(`[data-msg-id="${CSS.escape(firstNewId)}"]`);
    if (node) el.scrollTop = node.offsetTop - el.offsetTop;
    else el.scrollTop = el.scrollHeight;
    setFirstNewId(null);
  }, [containerRef, firstNewId]);

  return { onScroll, firstNewId, jumpToFirstNew, nearBottom };
}
